// starShaders.ts — GLSL for a procedural star: an emissive surface shaded by
// animated 3D fBm value-noise (granulation cells + brighter spots) with limb
// darkening, plus an additive fresnel corona shell for the star's atmosphere.
// Both are toneMapped:false so they stay bright under the Canvas's ACES mapping.

const NOISE = /* glsl */ `
  float hash(vec3 p) {
    p = fract(p * 0.3183099 + 0.1);
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }
  float vnoise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(mix(hash(i + vec3(0.0, 0.0, 0.0)), hash(i + vec3(1.0, 0.0, 0.0)), f.x),
          mix(hash(i + vec3(0.0, 1.0, 0.0)), hash(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
      mix(mix(hash(i + vec3(0.0, 0.0, 1.0)), hash(i + vec3(1.0, 0.0, 1.0)), f.x),
          mix(hash(i + vec3(0.0, 1.0, 1.0)), hash(i + vec3(1.0, 1.0, 1.0)), f.x), f.y), f.z);
  }
  float fbm(vec3 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 3; i++) {
      v += a * vnoise(p);
      p *= 2.0;
      a *= 0.5;
    }
    return v;
  }
`;

export const STAR_SURFACE_VERT = /* glsl */ `
  varying vec3 vPos;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vPos = position;
    vNormal = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vView = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

export const STAR_SURFACE_FRAG = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform float uSeed;
  uniform vec3 uHot;
  uniform vec3 uCool;
  varying vec3 vPos;
  varying vec3 vNormal;
  varying vec3 vView;
  ${NOISE}
  void main() {
    vec3 p = normalize(vPos) * 2.6 + uSeed;
    // Convective churn: a slowly-evolving low-frequency flow warps the sample so the
    // granulation cells boil, merge and split (turbulent convection) instead of
    // rigidly scrolling across the surface.
    vec3 flow = vec3(
      fbm(p * 0.8 + vec3(uTime * 0.05, 0.0, 0.0)),
      fbm(p * 0.8 + vec3(0.0, 7.3, uTime * 0.045)),
      fbm(p * 0.8 + vec3(3.1, -uTime * 0.04, 0.0))
    ) - 0.5;
    vec3 q = p + flow * 1.4;
    float granules = fbm(q * 1.5 + vec3(0.0, uTime * 0.02, 0.0));
    float cells = fbm(q * 4.0 - uTime * 0.03);
    float h = clamp(granules * 0.65 + cells * 0.35, 0.0, 1.0);
    vec3 col = mix(uCool, uHot, smoothstep(0.25, 0.85, h));
    // Brighter flare spots.
    col += uHot * pow(clamp(cells, 0.0, 1.0), 5.0) * 0.6;
    // Limb darkening: dimmer toward the silhouette.
    float limb = clamp(dot(normalize(vNormal), vView), 0.0, 1.0);
    col *= mix(0.5, 1.2, pow(limb, 0.55));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export const STAR_CORONA_VERT = /* glsl */ `
  varying vec3 vWorldPos;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

// Limb-hugging glow via the view ray's impact parameter (closest approach to the
// star centre): brightest at the star's surface radius, fading with altitude — so
// it's a corona hugging the limb, not a ring on the shell's own silhouette.
export const STAR_CORONA_FRAG = /* glsl */ `
  precision highp float;
  uniform vec3 uCameraPos;
  uniform vec3 uCenter;
  uniform float uStarRadius;
  uniform float uCoronaRadius;
  uniform vec3 uColor;
  uniform float uIntensity;
  uniform float uFalloff;
  varying vec3 vWorldPos;
  void main() {
    vec3 d = normalize(vWorldPos - uCameraPos);
    float tca = max(dot(uCenter - uCameraPos, d), 0.0);
    vec3 closest = uCameraPos + d * tca;
    float b = length(uCenter - closest);
    float band = max(uCoronaRadius - uStarRadius, 1e-4);
    float density = exp(-abs(b - uStarRadius) / band * uFalloff);
    float a = clamp(density * uIntensity, 0.0, 1.0);
    gl_FragColor = vec4(uColor * a, a);
  }
`;
